/**
 * Pricing → Estadísticas (`/pricing/estadisticas`).
 *
 * Consolidado del histórico que publica el bot de *scraping* —estaciones y
 * cargas leídas, precio promedio, mínimo y máximo, y el detalle por categoría y
 * producto— con el filtro compartido de zona (ubigeo) y periodo (año/mes).
 *
 * La pantalla está pensada para ir de lo general a lo concreto: sin filtros se
 * ve todo el país y, según se elige departamento, provincia, distrito, año y
 * mes, la API consolida sólo los ficheros que caen en ese filtro.
 */

import {
  ArrowLeft,
  BarChart3,
  Building2,
  CircleDollarSign,
  FileText,
  Fuel,
  Layers,
  Loader2,
  MapPin,
  RotateCcw,
  SearchX,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { FiltrosUbicacionCard } from '../components/filtros-ubicacion';
import { useFiltrosUbicacion } from '../hooks/use-filtros-ubicacion';
import { fechaCorta } from '../lib/periodos';
import { usePricingEstadisticas } from '../pricing.api';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Resumen de una lista de precios. */
function resumenPrecios(precios: number[]) {
  if (precios.length === 0) return { promedio: 0, minimo: 0, maximo: 0 };

  const suma = precios.reduce((total, precio) => total + precio, 0);

  return {
    promedio: suma / precios.length,
    minimo: Math.min(...precios),
    maximo: Math.max(...precios),
  };
}

const TONOS = {
  brand: 'bg-brand-50 text-brand-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  sky: 'bg-sky-50 text-sky-600',
} as const;

/** Barra de las tablas de producto (una por categoría). */
const BARRAS = ['bg-brand-400', 'bg-emerald-400', 'bg-amber-400', 'bg-sky-400', 'bg-violet-400'];

/** Indicador de la fila superior. */
function Indicador({
  etiqueta,
  valor,
  detalle,
  icono,
  tono,
}: {
  etiqueta: string;
  valor: string;
  detalle: string;
  icono: ReactNode;
  tono: keyof typeof TONOS;
}) {
  return (
    <div className="rounded-card border border-ink-100 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            {etiqueta}
          </span>
          <span className="mt-1 block truncate text-xl font-bold tabular-nums text-ink-900">
            {valor}
          </span>
          <span className="mt-0.5 block text-[11px] text-ink-400">{detalle}</span>
        </div>
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            TONOS[tono],
          )}
          aria-hidden="true"
        >
          {icono}
        </span>
      </div>
    </div>
  );
}

export function PricingEstadisticasPage() {
  useDocumentTitle('Pricing · Estadísticas');

  const filtro = useFiltrosUbicacion();
  const consulta = usePricingEstadisticas(filtro.filtros);

  const datos = consulta.data?.estadisticas;
  const categorias = Object.entries(datos?.categorias ?? {});

  // Escala de las barras: el producto con más estaciones manda.
  const maximoEstaciones = Math.max(
    1,
    ...categorias.flatMap(([, productos]) =>
      Object.values(productos).map((producto) => producto.cantidad),
    ),
  );

  const totalProductos = categorias.reduce(
    (total, [, productos]) => total + Object.keys(productos).length,
    0,
  );

  const sinDatos = !consulta.isLoading && (datos?.totalCargas ?? 0) === 0;
  const actualizado = fechaCorta(datos?.lastUpdate);

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/pricing"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Pricing
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <BarChart3 className="size-6 text-brand-500" aria-hidden="true" />
          Estadísticas
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Elige una zona y un periodo, y revisa sus precios por producto: promedio, mínimo y máximo
          sobre el histórico de precios.
        </p>
      </div>

      {datos ? (
        <div className="flex flex-wrap items-center gap-2">
          {consulta.isFetching ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-ink-400" role="status">
              <Loader2 className="size-3 animate-spin" aria-hidden="true" />
              Actualizando…
            </span>
          ) : null}
          <Badge variant="brand">
            <MapPin className="mr-1 size-3" aria-hidden="true" />
            {filtro.alcance}
          </Badge>
          {filtro.periodo ? <Badge variant="outline">{filtro.periodo}</Badge> : null}
          <Badge variant="outline">
            {formatNumber(datos.archivosMostrados)} de {formatNumber(datos.archivosProcessados)}{' '}
            archivos
          </Badge>
        </div>
      ) : null}
    </header>
  );

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="De dónde sale el consolidado">
        El consolidado sale del histórico de precios, que se guarda por ubicación y fecha. El
        promedio general es el del servicio y el de cada producto se calcula con sus lecturas.
        Puedes combinar una zona con un año (y, si quieres, un mes) para analizar solo ese tramo.
      </Alert>

      <FiltrosUbicacionCard
        valores={filtro}
        anios={datos?.anios ?? []}
        departamentosConDatos={datos?.departamentos ?? []}
      />

      {consulta.error && !consulta.data ? (
        <Alert variant="warning" title="Servicio de precios no disponible">
          <span className={ICONO_EN_ALERTA}>
            <CircleDollarSign className="size-4" aria-hidden="true" />
            {mensajeDe(
              consulta.error,
              'No se pudieron leer las estadísticas del servicio de precios.',
            )}
          </span>
        </Alert>
      ) : null}

      {consulta.isLoading && !datos ? (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, indice) => (
            <Skeleton key={indice} className="h-24 w-full" />
          ))}
        </div>
      ) : datos && !sinDatos ? (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Indicador
              etiqueta="Estaciones únicas"
              valor={formatNumber(datos.totalEstaciones)}
              detalle={`${formatNumber(datos.totalCargas)} lecturas con precio`}
              icono={<Building2 className="size-4" />}
              tono="brand"
            />
            <Indicador
              etiqueta="Precio promedio"
              valor={formatCurrency(datos.precioPromedio)}
              detalle={`mínimo ${formatCurrency(datos.precioMinimo)} · máximo ${formatCurrency(
                datos.precioMaximo,
              )}`}
              icono={<CircleDollarSign className="size-4" />}
              tono="emerald"
            />
            <Indicador
              etiqueta="Categorías"
              valor={formatNumber(categorias.length)}
              detalle={`${formatNumber(totalProductos)} productos distintos`}
              icono={<Layers className="size-4" />}
              tono="amber"
            />
            <Indicador
              etiqueta="Archivos"
              valor={`${formatNumber(datos.archivosMostrados)} / ${formatNumber(
                datos.archivosProcessados,
              )}`}
              detalle={actualizado ? `Última lectura: ${actualizado}` : 'Sin fecha de lectura'}
              icono={<FileText className="size-4" />}
              tono="sky"
            />
          </div>

          {categorias.map(([categoria, productos], indiceCategoria) => {
            const lista = Object.entries(productos).sort((a, b) => b[1].cantidad - a[1].cantidad);
            const lecturas = lista.reduce((total, [, resumen]) => total + resumen.cantidad, 0);

            return (
              <Card key={categoria}>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <Fuel className="size-4 text-brand-500" aria-hidden="true" />
                      {categoria}
                    </h2>
                    <span className="text-[11px] text-ink-400">
                      {formatNumber(lista.length)} productos · {formatNumber(lecturas)} lecturas
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                          <th className="py-2">Producto</th>
                          <th className="py-2 text-right">Estaciones</th>
                          <th className="py-2 text-right">Lecturas</th>
                          <th className="py-2 text-right">Promedio</th>
                          <th className="py-2 text-right">Mínimo</th>
                          <th className="py-2 text-right">Máximo</th>
                          <th className="w-40 py-2">Participación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lista.map(([producto, resumen]) => {
                          const calculado = resumenPrecios(resumen.precios);
                          const participacion = lecturas
                            ? (resumen.cantidad / lecturas) * 100
                            : 0;

                          return (
                            <tr
                              key={producto}
                              className="border-b border-ink-50 transition-colors last:border-0 hover:bg-brand-50/40"
                            >
                              <td className="py-2 text-ink-800">{producto}</td>
                              <td className="py-2 text-right tabular-nums text-ink-700">
                                {formatNumber(resumen.estaciones ?? 0)}
                              </td>
                              <td className="py-2 text-right tabular-nums text-ink-500">
                                {formatNumber(resumen.cantidad)}
                              </td>
                              <td className="py-2 text-right font-semibold tabular-nums text-ink-900">
                                {formatCurrency(calculado.promedio)}
                              </td>
                              <td className="py-2 text-right tabular-nums text-ink-500">
                                {formatCurrency(calculado.minimo)}
                              </td>
                              <td className="py-2 text-right tabular-nums text-ink-500">
                                {formatCurrency(calculado.maximo)}
                              </td>
                              <td className="py-2">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="block h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-ink-100"
                                    role="presentation"
                                  >
                                    <span
                                      className={cn(
                                        'block h-full rounded-full',
                                        BARRAS[indiceCategoria % BARRAS.length],
                                      )}
                                      style={{
                                        width: `${Math.max(
                                          2,
                                          (resumen.cantidad / maximoEstaciones) * 100,
                                        )}%`,
                                      }}
                                    />
                                  </span>
                                  <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-ink-400">
                                    {participacion.toFixed(1)}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-ink-100 text-ink-400">
              <SearchX className="size-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-semibold text-ink-900">
                {filtro.hayFiltro
                  ? `Sin precios para ${filtro.alcance}${
                      filtro.periodo ? ` en ${filtro.periodo}` : ''
                    }`
                  : 'Todavía no hay precios procesados'}
              </p>
              <p className="mt-1 max-w-md text-xs text-ink-500">
                {filtro.hayFiltro
                  ? 'Esa zona o periodo no tiene lecturas en el histórico. Prueba con otro distrito, con la provincia completa, con otro mes o vuelve a todo el histórico.'
                  : 'Cuando se complete una consulta, aquí aparecerá el consolidado de sus precios.'}
              </p>
            </div>
            {filtro.hayFiltro ? (
              <Button variant="outline" size="sm" onClick={filtro.limpiar}>
                <RotateCcw className="mr-1.5 size-3.5" aria-hidden="true" />
                Limpiar filtros
              </Button>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
